import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/** 로그인·온보딩처럼 가운데 카드 하나로 끝나는 화면. */
export function CenterCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-5 py-12">
      <div className="w-full max-w-[380px] rounded-2xl bg-surface p-8 shadow-card">{children}</div>
    </div>
  );
}

export function Logo({ sub }: { sub?: string }) {
  return (
    <div className="mb-7 text-center">
      <div className="text-[22px] font-semibold tracking-tight text-ink-900">
        <span className="mr-1">🍅</span>토마토 마켓
      </div>
      {sub && <p className="mt-2 text-[13px] leading-relaxed text-ink-500">{sub}</p>}
    </div>
  );
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between">
      <span className="text-[13px] font-medium text-ink-700">{children}</span>
      {hint && <span className="text-[12px] tabular-nums text-ink-400">{hint}</span>}
    </div>
  );
}

const fieldBase =
  'w-full rounded-xl bg-ink-50 px-3.5 py-3 text-[15px] text-ink-900 outline-none transition placeholder:text-ink-300 hairline focus:bg-surface focus:shadow-[inset_0_0_0_1.5px_var(--color-tomato-500)] disabled:text-ink-400';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
}

export function TextField({ className = '', ...props }: TextFieldProps) {
  return <input {...props} className={`${fieldBase} ${className}`} />;
}

export function TextArea({ className = '', ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${fieldBase} resize-none leading-relaxed ${className}`} />;
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${fieldBase} appearance-none pr-9 ${className}`} />;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'sm';
}

const variants = {
  primary:
    'bg-tomato-600 text-white hover:bg-tomato-700 active:scale-[0.98] disabled:bg-ink-100 disabled:text-ink-300 disabled:active:scale-100',
  secondary:
    'bg-ink-50 text-ink-700 hairline hover:bg-ink-100 active:scale-[0.98] disabled:text-ink-300',
  ghost: 'text-ink-500 hover:text-ink-900 disabled:text-ink-300',
  danger: 'text-tomato-600 hover:bg-tomato-50 disabled:text-ink-300',
};

export function Button({ variant = 'primary', size = 'md', className = '', ...props }: ButtonProps) {
  const sizing = size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-4 py-3 text-[15px]';
  return (
    <button
      {...props}
      className={`rounded-xl font-medium transition duration-200 ease-(--ease-out-soft) disabled:cursor-not-allowed ${sizing} ${variants[variant]} ${className}`}
    />
  );
}

export function Notice({ tone = 'error', children }: { tone?: 'error' | 'info'; children?: ReactNode }) {
  if (!children) return null;
  const styles = tone === 'error' ? 'bg-tomato-50 text-tomato-700' : 'bg-ink-50 text-ink-500';
  return <p className={`mt-3 rounded-xl px-3.5 py-2.5 text-[13px] ${styles}`}>{children}</p>;
}

export function Checkbox({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label
      className={`flex items-start gap-2.5 text-[13px] leading-relaxed ${
        disabled ? 'cursor-not-allowed text-ink-300' : 'cursor-pointer text-ink-700'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-[18px] shrink-0 rounded-md accent-tomato-600"
      />
      <span>{children}</span>
    </label>
  );
}

/** 카테고리·거래유형 같은 짧은 라벨. */
export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'accent'; children: ReactNode }) {
  const styles = tone === 'accent' ? 'bg-tomato-50 text-tomato-700' : 'bg-ink-100 text-ink-500';
  return (
    <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ${styles}`}>{children}</span>
  );
}

export function Spinner() {
  return (
    <span className="inline-block size-4 animate-spin rounded-full border-2 border-ink-200 border-t-ink-500" />
  );
}

/**
 * 스켈레톤 — 로딩 중에도 화면 골격이 서 있게(스피너 대신). 콘텐츠가 툭 나타나며
 * 레이아웃이 튀는 걸 막는다. 은은한 pulse만 쓰고 shimmer는 쓰지 않는다(과함).
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden className={`animate-pulse rounded-lg bg-ink-100 ${className}`} />;
}

/** 글 목록 스켈레톤 — PostRow(뱃지+제목 / 요약 / 메타 + 썸네일)의 골격을 흉내낸다. */
export function PostListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-surface px-4 shadow-card" role="status" aria-label="불러오는 중">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i}>
          {i > 0 && <div className="h-px bg-ink-100" />}
          <div className="flex gap-3 px-1 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-9" />
                <Skeleton className="h-4 w-3/5" />
              </div>
              <Skeleton className="mt-2 h-3.5 w-11/12" />
              <Skeleton className="mt-1.5 h-3.5 w-2/3" />
              <Skeleton className="mt-2.5 h-3 w-2/5" />
            </div>
            <Skeleton className="size-16 shrink-0 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** 글 상세 스켈레톤 — 제목/작성자/본문 골격. */
export function PostDetailSkeleton() {
  return (
    <div className="py-6" role="status" aria-label="불러오는 중">
      <Skeleton className="h-3.5 w-16" />
      <Skeleton className="mt-5 h-7 w-4/5" />
      <Skeleton className="mt-3 h-3.5 w-2/5" />
      <Skeleton className="mt-6 h-52 w-full rounded-2xl" />
      <Skeleton className="mt-4 h-4 w-full" />
      <Skeleton className="mt-2 h-4 w-11/12" />
      <Skeleton className="mt-2 h-4 w-3/5" />
    </div>
  );
}

export function EmptyState({ icon, title, body }: { icon: string; title: string; body?: string }) {
  return (
    <div className="py-24 text-center">
      {/* 이모지를 은은한 원판 위에 — 허전한 빈 화면에 초점 하나를 만든다. */}
      <div className="mx-auto flex size-20 items-center justify-center rounded-full bg-ink-100/60">
        <span className="text-[36px] leading-none">{icon}</span>
      </div>
      <p className="mt-5 text-[15px] font-medium text-ink-700">{title}</p>
      {body && <p className="mt-1.5 text-[13px] text-ink-400">{body}</p>}
    </div>
  );
}
